import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isString from 'lodash/isString';

import { useFetch, useMutation } from 'utils/hooks';
import { scrollToTop } from 'utils/methods';
import { CustomToast } from 'components';
import {
  createFormData,
  createErrorData,
  validateFormData,
  createMutationPayload,
  createServerError
} from './methods';
import { DEFAULT_FORM_DATA } from './stubs';
import {
  ROUTES,
  PRODUCT_SKU_CONFIG_KEYS as DATA_KEYS
} from '../../../../stubs';

const SkuConfigContext = createContext({
  data: {},
  isError: false,
  isLoading: false,
  fetchData: () => {},
  isEditMode: false,
  formData: {},
  onFormDataChange: () => {},
  setInitialProducts: () => {},
  errors: {},
  isProcessing: false,
  onSave: () => {}
});

const API_KEY = 'configure.serviceWindow.exceptions.productSkus.root';

export function SkuConfigProvider({ children = null }) {
  const { id: exceptionId } = useParams();
  const { replace } = useHistory();
  const isEditMode = !!exceptionId;

  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const initialFormDataRef = useRef(DEFAULT_FORM_DATA);
  const [errors, setErrors] = useState({});

  const { isLoading, isError, fetchData } = useFetch(API_KEY, {
    initialData: DEFAULT_FORM_DATA,
    loadOnMount: isEditMode,
    routeParam: exceptionId,
    errorMessage:
      'Unable to fetch the return window exception data. Please try later.',
    onSuccess: useCallback(data => {
      const formData = createFormData(data);
      initialFormDataRef.current = formData;
      setFormData(formData);
    }, [])
  });

  const { mutate, isProcessing } = useMutation(
    API_KEY,
    isEditMode ? 'PATCH' : 'POST',
    {
      routeParam: isEditMode ? exceptionId : '',
      successMessage: `Successfully ${
        isEditMode ? 'updated' : 'created'
      } the return window exception.`,
      payload: useMemo(() => createMutationPayload(formData), [formData]),
      onValidate: useCallback(() => {
        const { errors, isValid } = validateFormData(formData);
        if (!isValid) {
          setErrors(errors);
          const timer = setTimeout(() => {
            scrollToTop();
            clearTimeout(timer);
          }, 0);
        }
        return isValid;
      }, [formData]),
      onSuccess: useCallback(() => {
        initialFormDataRef.current = formData;
        const timer = setTimeout(() => {
          replace(ROUTES.PRODUCT_SKU_EXCEPTION);
          clearTimeout(timer);
        }, 10);
      }, [formData, replace]),
      onError: useCallback(
        (error, errorData) => {
          scrollToTop();
          const errors = createServerError(errorData);
          setErrors(errors);
          if (_isEmpty(errorData)) {
            CustomToast({
              isNotified: _get(error, 'notified', false),
              type: 'error',
              msg: `Unable to ${
                isEditMode ? 'update' : 'create'
              } the return window exception at the moment.`
            });
          }
        },
        [isEditMode]
      )
    }
  );

  const onFormDataChange = useCallback((key, value) => {
    if (!key || !_isString(key)) return;
    setFormData(currData => ({ ...currData, [key]: value }));
    setErrors(currErrors => createErrorData(currErrors, key, value));
  }, []);

  const setInitialProducts = useCallback(
    products => {
      if (!isEditMode) return;
      initialFormDataRef.current[DATA_KEYS.PRODUCTS] = products;
      setFormData(currData => {
        return { ...currData, [DATA_KEYS.PRODUCTS]: products };
      });
    },
    [isEditMode]
  );

  return (
    <SkuConfigContext.Provider
      value={{
        isError,
        isLoading,
        fetchData,
        initialData: initialFormDataRef.current,
        isEditMode,
        errors,
        formData,
        onFormDataChange,
        setInitialProducts,
        isProcessing,
        onSave: mutate
      }}
    >
      {children}
    </SkuConfigContext.Provider>
  );
}

export function useProductSkuConfig() {
  return useContext(SkuConfigContext);
}
